import { useMemo, useState } from 'react';
import { useClientStore } from '@core/client/useClientStore';
import { Invite, User, UserRole } from '@core/generated/gql-types';

export type SiteAssignmentOption =
  | { type: 'user'; id: string; email: string; user: User }
  | { type: 'invite'; id: string; email: string; invite: Invite };

export const getUserDisplayName = (user: User) => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName || '';
};

// Combine and sort the site assignment options for the client users and pending invites.
// This is used to display the options in a single unified list.
export const combineAndSortAssignmentOptions = (
  clientUsers: User[],
  pendingInvites: Invite[],
): SiteAssignmentOption[] => {
  const userOptions: SiteAssignmentOption[] = clientUsers.map((user) => ({
    type: 'user',
    id: user.id,
    email: user.email ?? '',
    user,
  }));
  const inviteOptions: SiteAssignmentOption[] = pendingInvites.map(
    (invite) => ({
      type: 'invite',
      id: invite.id,
      email: invite.email ?? '',
      invite,
    }),
  );
  return [...userOptions, ...inviteOptions].sort((firstOption, secondOption) =>
    firstOption.email.localeCompare(secondOption.email),
  );
};

interface UseSiteAssignmentProps {
  clientId: string;
  // When provided, the hook can derive which users/invites are already assigned
  // to the site (used for seeding the selection and rendering the current set).
  siteId?: string;
}

/**
 * Encapsulates the logic for choosing which Client Users / pending Client User
 * invites are assigned to a site: filtering the relevant accounts for a client,
 * combining and sorting them into checkbox options, and tracking the selection.
 *
 * Used by both the create flow (SiteModal) and the edit flow (ClientSiteDetails).
 * It reads users and invites from the client store; the consuming component is
 * responsible for ensuring those have been fetched.
 */
const useSiteAssignment = ({ clientId, siteId }: UseSiteAssignmentProps) => {
  const { users, invites } = useClientStore();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedInviteIds, setSelectedInviteIds] = useState<string[]>([]);

  // Client Users (not Client Admins) belonging to this client. These are the
  // only accounts whose access can be scoped to a site via siteIds.
  const clientUsers = useMemo<User[]>(
    () =>
      users.filter(
        (user) =>
          user.role === UserRole.ClientUser && user.clientId === clientId,
      ),
    [users, clientId],
  );

  // Pending Client User invites for this client. Invites carry their own siteIds,
  // which are copied onto the user once accepted, so their site access can be
  // modified before they sign up.
  const pendingInvites = useMemo<Invite[]>(
    () =>
      invites.filter(
        (invite) =>
          invite.userRole === UserRole.ClientUser &&
          invite.clientId === clientId,
      ),
    [invites, clientId],
  );

  // Client Users currently assigned to this site (site is in their siteIds).
  const assignedUsers = useMemo<User[]>(
    () =>
      siteId
        ? clientUsers.filter((user) => user.siteIds?.includes(siteId))
        : [],
    [clientUsers, siteId],
  );

  // Pending invites currently assigned to this site (site is in their siteIds).
  const assignedInvites = useMemo<Invite[]>(
    () =>
      siteId
        ? pendingInvites.filter((invite) => invite.siteIds?.includes(siteId))
        : [],
    [pendingInvites, siteId],
  );

  // Every assignable account/invite for the client, as selectable options.
  const siteAssignmentOptions = useMemo<SiteAssignmentOption[]>(
    () => combineAndSortAssignmentOptions(clientUsers, pendingInvites),
    [clientUsers, pendingInvites],
  );

  // Only the accounts/invites already assigned to the site, as options.
  const allAssignableOptions = useMemo<SiteAssignmentOption[]>(
    () => combineAndSortAssignmentOptions(assignedUsers, assignedInvites),
    [assignedUsers, assignedInvites],
  );

  const toggleSelectedUser = (userId: string) => {
    setSelectedUserIds(
      (currentIds) =>
        currentIds.includes(userId)
          ? currentIds.filter((selectedId) => selectedId !== userId) // Remove the user from the selected IDs.
          : [...currentIds, userId], // Add the user to the selected IDs.
    );
  };

  const toggleSelectedInvite = (inviteId: string) => {
    setSelectedInviteIds(
      (currentIds) =>
        currentIds.includes(inviteId)
          ? currentIds.filter((selectedId) => selectedId !== inviteId) // Remove the invite from the selected IDs.
          : [...currentIds, inviteId], // Add the invite to the selected IDs.
    );
  };

  const selectAllAssignable = () => {
    setSelectedUserIds(clientUsers.map((user) => user.id));
    setSelectedInviteIds(pendingInvites.map((invite) => invite.id));
  };

  const clearSelection = () => {
    setSelectedUserIds([]);
    setSelectedInviteIds([]);
  };

  // Seed the selection with the users/invites already assigned to the site.
  const resetSelectionToAssigned = () => {
    setSelectedUserIds(assignedUsers.map((user) => user.id));
    setSelectedInviteIds(assignedInvites.map((invite) => invite.id));
  };

  const selectedOptionsCount =
    selectedUserIds.length + selectedInviteIds.length;

  return {
    clientUsers,
    pendingInvites,
    assignedUsers,
    assignedInvites,
    siteAssignmentOptions,
    allAssignableOptions,
    selectedUserIds,
    selectedInviteIds,
    selectedOptionsCount,
    toggleSelectedUser,
    toggleSelectedInvite,
    selectAllAssignable,
    clearSelection,
    resetSelectionToAssigned,
  };
};

export default useSiteAssignment;
